import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { FeedbackMetrics } from '@/types';

interface FeedbackMetricsProps {
  metrics: FeedbackMetrics;
}

// The panel's own short labels — the detail page's `qualityLabels` prop spells the same
// dimensions out in full. Both copies exist in the Next tree (PORTING.md U8).
const DIMENSION_LABELS: Record<string, string> = {
  oficina: 'Oficina',
  wifi: 'WiFi',
  comida: 'Comida',
  software: 'Software',
  comunicacion: 'Comunicación',
  branding: 'Branding',
  mentores: 'Mentores',
  jueces: 'Jueces',
  sponsors: 'Sponsors',
  nivelTecnico: 'Nivel técnico',
  tracks: 'Tracks',
  premios: 'Premios',
  procesoEvaluacion: 'Proceso evaluación',
  publicVoting: 'Public voting',
  organizacion: 'Organización',
  platanusHackMarket: 'Platanus Hack Market',
};

function formatAverage(avg: number | null): string {
  if (avg == null) return '—';
  return avg.toFixed(2);
}

export function FeedbackMetricsPanel({ metrics }: FeedbackMetricsProps) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-md border bg-card"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 p-4 text-left"
        >
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wide">
                Responses
              </div>
              <div className="font-semibold text-2xl">
                {metrics.totalResponses}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wide">
                NPS
              </div>
              <div className="font-semibold text-2xl">
                {metrics.npsScore ?? '—'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wide">
                Overall avg
              </div>
              <div className="font-semibold text-2xl">
                {formatAverage(metrics.overallAverage)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wide">
                Aura avg
              </div>
              <div className="font-semibold text-2xl">
                {formatAverage(metrics.auraAverage)}
              </div>
            </div>
            <div className="text-muted-foreground text-xs">
              <span className="text-green-600">
                Promoters {metrics.npsPromoters}
              </span>
              {' · '}
              <span>Passives {metrics.npsPassives}</span>
              {' · '}
              <span className="text-red-600">
                Detractors {metrics.npsDetractors}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <span className="flex items-center gap-1">
              {open ? 'Hide details' : 'Show details'}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </span>
          </Button>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-t p-4">
          <div className="mb-3 font-medium text-sm">
            Per-dimension averages (1–5)
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {metrics.dimensionAverages.map((d) => (
              <div
                key={d.dimension}
                className="rounded border bg-background p-3"
              >
                <div className="text-muted-foreground text-xs">
                  {DIMENSION_LABELS[d.dimension] ?? d.dimension}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-lg">
                    {formatAverage(d.average)}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    n={d.count}
                  </span>
                </div>
              </div>
            ))}
            {metrics.hackRacerAverage != null && (
              <div className="rounded border bg-background p-3">
                <div className="text-muted-foreground text-xs">
                  Platanus Hack Racer (1–5)
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-lg">
                    {formatAverage(metrics.hackRacerAverage)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
